import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import type {
  ActionResult,
  BlockedNumber,
  Carrier,
  Dashboard,
  DashboardFilters,
  EmailMessage,
  Parcel,
  ParcelCreate,
  ParcelDetail,
  ParcelPatch,
  Project,
  PushConfig,
  SyncStatus,
} from './types'

const BASE = '/api'

/**
 * Notified when the API says 401.
 *
 * A session can expire at any moment, including between two background refetches. Rather
 * than have every call site handle that, the transport reports it once and `App` re-checks
 * the session, which flips the whole UI back to the login screen.
 */
let unauthorizedHandler: (() => void) | null = null

export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler
}

export class ApiError extends Error {
  // Declared explicitly rather than as a parameter property: the project builds with
  // `erasableSyntaxOnly`, which disallows TypeScript-only constructor syntax.
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.()
    // FastAPI puts the human-readable reason in `detail`; surface it rather than a code.
    let message = `HTTP ${response.status}`
    try {
      const body = await response.json()
      if (typeof body.detail === 'string') message = body.detail
      else if (Array.isArray(body.detail) && body.detail[0]?.msg) message = body.detail[0].msg
    } catch {
      /* response had no JSON body */
    }
    throw new ApiError(message, response.status)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function dashboardParams(filters: DashboardFilters): string {
  const params = new URLSearchParams()
  if (filters.projectId != null) params.set('project_id', String(filters.projectId))
  if (filters.unassigned) params.set('unassigned', 'true')
  if (filters.carrier) params.set('carrier', filters.carrier)
  if (filters.q?.trim()) params.set('q', filters.q.trim())
  if (filters.direction) params.set('direction', filters.direction)
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const keys = {
  dashboard: (filters: DashboardFilters) => ['dashboard', filters] as const,
  parcel: (id: number) => ['parcel', id] as const,
  projects: (includeInactive: boolean) => ['projects', includeInactive] as const,
  carriers: ['carriers'] as const,
  syncStatus: ['sync-status'] as const,
  emails: (parseStatus?: string) => ['emails', parseStatus ?? 'all'] as const,
  blockedNumbers: ['blocked-numbers'] as const,
  pushConfig: ['push-config'] as const,
}

// --- Queries -------------------------------------------------------------

export function useDashboard(filters: DashboardFilters): UseQueryResult<Dashboard> {
  return useQuery({
    queryKey: keys.dashboard(filters),
    queryFn: () => request<Dashboard>(`/dashboard${dashboardParams(filters)}`),
    // Parcel status changes on the carrier's clock, not the user's: refresh quietly.
    refetchInterval: 60_000,
    placeholderData: (previous) => previous,
  })
}

export function useParcel(id: number | null) {
  return useQuery({
    queryKey: keys.parcel(id ?? -1),
    queryFn: () => request<ParcelDetail>(`/parcels/${id}`),
    enabled: id != null,
  })
}

export function useProjects(includeInactive = false) {
  return useQuery({
    queryKey: keys.projects(includeInactive),
    queryFn: () =>
      request<Project[]>(`/projects${includeInactive ? '?include_inactive=true' : ''}`),
  })
}

export function useCarriers() {
  return useQuery({
    queryKey: keys.carriers,
    queryFn: () => request<Carrier[]>('/carriers'),
    staleTime: 60 * 60_000, // reference data
  })
}

export function useSyncStatus() {
  return useQuery({
    queryKey: keys.syncStatus,
    queryFn: () => request<SyncStatus>('/sync/status'),
    refetchInterval: 30_000,
  })
}

export function useEmails(parseStatus?: string) {
  return useQuery({
    queryKey: keys.emails(parseStatus),
    queryFn: () =>
      request<{ items: EmailMessage[]; total: number }>(
        `/emails${parseStatus ? `?parse_status=${parseStatus}` : ''}`,
      ),
  })
}

// --- Mutations -----------------------------------------------------------

/** Anything that changes a parcel invalidates the board and that parcel's detail. */
function useParcelInvalidation() {
  const queryClient = useQueryClient()
  return (id?: number) => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
    if (id != null) void queryClient.invalidateQueries({ queryKey: keys.parcel(id) })
  }
}

export function useCreateParcel() {
  const invalidate = useParcelInvalidation()
  return useMutation({
    mutationFn: (payload: ParcelCreate) =>
      request<ParcelDetail>('/parcels', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (parcel) => invalidate(parcel.id),
  })
}

export function useUpdateParcel() {
  const invalidate = useParcelInvalidation()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ParcelPatch }) =>
      request<ParcelDetail>(`/parcels/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (parcel) => invalidate(parcel.id),
  })
}

export function useRefreshParcel() {
  const invalidate = useParcelInvalidation()
  return useMutation({
    mutationFn: (id: number) =>
      request<ActionResult>(`/parcels/${id}/refresh`, { method: 'POST' }),
    onSuccess: (_result, id) => invalidate(id),
  })
}

/** Say by hand that a parcel arrived — the only way for the carriers without an API. */
export function useMarkDelivered() {
  const invalidate = useParcelInvalidation()
  return useMutation({
    mutationFn: (id: number) =>
      request<ParcelDetail>(`/parcels/${id}/delivered`, { method: 'POST' }),
    onSuccess: (parcel) => invalidate(parcel.id),
  })
}

/** Star a parcel for the signed-in person, or take the star off. */
export function useSetFavorite() {
  const invalidate = useParcelInvalidation()
  return useMutation({
    mutationFn: ({ id, favorite }: { id: number; favorite: boolean }) =>
      request<ParcelDetail>(`/parcels/${id}/favorite`, {
        method: 'POST',
        body: JSON.stringify({ favorite }),
      }),
    onSuccess: (parcel) => invalidate(parcel.id),
  })
}

export function usePushConfig() {
  return useQuery({
    queryKey: keys.pushConfig,
    queryFn: () => request<PushConfig>('/push/config'),
  })
}

/** Hide a parcel from everyone but administrators, or put it back on the board. */
export function useSetParcelHidden() {
  const invalidate = useParcelInvalidation()
  return useMutation({
    mutationFn: ({ id, hidden }: { id: number; hidden: boolean }) =>
      request<ParcelDetail>(`/parcels/${id}/hidden`, {
        method: 'POST',
        body: JSON.stringify({ hidden }),
      }),
    onSuccess: (parcel) => invalidate(parcel.id),
  })
}

/** Delete a parcel for good. The backend blocks its number against being filed again. */
export function useDeleteParcel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => request<void>(`/parcels/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: keys.blockedNumbers })
    },
  })
}

export function useBlockedNumbers(enabled: boolean) {
  return useQuery({
    queryKey: keys.blockedNumbers,
    queryFn: () => request<BlockedNumber[]>('/blocked-numbers'),
    enabled,
  })
}

export function useUnblockNumber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (trackingNumber: string) =>
      request<void>(`/blocked-numbers/${encodeURIComponent(trackingNumber)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.blockedNumbers }),
  })
}

export function useCreateProject() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { name: string; customer?: string; description?: string }) =>
      request<Project>('/projects', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  })
}

export function useUpdateProject() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Project> }) =>
      request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteProject() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => request<Project>(`/projects/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useTriggerSync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (what: 'emails' | 'carriers') =>
      request<ActionResult>(`/sync/${what}`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: keys.syncStatus })
      void queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function useReparseEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      request<ActionResult>(`/emails/${id}/reparse`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['emails'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export type { Parcel }
